import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { FileText, BarChart3, Search, TrendingUp } from 'lucide-react';
import { useToast } from '@chakra-ui/react';
import { FaEnvelope, FaPhone, FaLinkedin } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { CONTACT_FORM_URL } from '../utils/constants';
import { BotTrapField, useFormBotTrap } from '../components/FormBotTrap';
import logger from '../utils/logger';
import { DemoSection } from '../components/marketing';
import { SiteNav } from '../components/SiteNav';
import { SiteFooter } from '../components/SiteFooter';

// Define utility function for conditional class names
const cn = (...classes: (string | boolean | undefined)[]) => {
  return classes.filter(Boolean).join(' ');
};

// Create animation components
const MotionDiv = motion.div;

// Site-wide CTA button styles. Designer review locked us into two
// variants only — primary (orange filled) and secondary (orange
// outline) — with subtle hovers (no color flip) and no trailing arrow
// icons. Both used on light AND dark backgrounds because orange has
// enough contrast against navy.
const btnPrimary =
  'inline-flex items-center justify-center bg-gsp-orange text-white font-semibold px-7 py-4 rounded-[10px] hover:bg-gsp-orange/90 transition-colors shadow-lg hover:shadow-xl';
const btnSecondary =
  'inline-flex items-center justify-center border-2 border-gsp-orange text-gsp-orange font-semibold px-7 py-4 rounded-[10px] hover:bg-gsp-orange/10 transition-colors';

// Section component for consistent styling
interface SectionProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  bgColor?: string;
}

function Section({ id, children, className, bgColor = 'bg-white' }: SectionProps) {
  return (
    <div
      id={id}
      className={cn(
        "relative scroll-mt-20 py-16 md:py-24",
        bgColor,
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </div>
  );
}

// Grid Background — mask uses symmetric 25%/75% stops so the fade-in zone
// above and fade-out zone below are exactly equal lengths (matches the
// Advisory hero treatment).
function GridBackground({ tone = 'subtle' }: { tone?: 'subtle' | 'visible' }) {
  const opacity = tone === 'visible' ? 'opacity-60' : 'opacity-[0.07]';
  const lineAlpha = tone === 'visible' ? 0.28 : 0.3;
  return (
    <div
      className={`absolute inset-0 pointer-events-none ${opacity}`}
      style={{
        backgroundImage: `
          linear-gradient(to right, rgba(51, 130, 156, ${lineAlpha}) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(51, 130, 156, ${lineAlpha}) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
      }}
    />
  );
}

// Diagonal Stripes Decoration (subtle GSP brand element)
function DiagonalStripes({ className = '' }: { className?: string }) {
  return (
    <div className={cn("absolute pointer-events-none opacity-10", className)}>
      <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
        <path d="M0 200L200 0V60L0 200Z" fill="#33829c"/>
        <path d="M60 200L200 60V120L120 200H60Z" fill="#33829c"/>
        <path d="M120 200L200 120V200H120Z" fill="#33829c"/>
      </svg>
    </div>
  );
}

function Landing() {
  // Form fields restructured to match the Advisory page's "Book a consult"
  // form (Name / Firm / Email / Role / Message). The Lambda contact endpoint
  // only knows about name / email / phone / message, so on submit we pack
  // firm + role into the message body the same way Consulting.tsx does.
  const [contactFormData, setContactFormData] = useState({
    name: '',
    firm: '',
    email: '',
    role: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const botTrap = useFormBotTrap();
  const [emailCopied, setEmailCopied] = useState(false);
  const toast = useToast();
  const location = useLocation();

  // Belt-and-suspenders for the email row: the `mailto:` href still fires
  // for users with a configured mail client, but we ALSO copy the address
  // to the clipboard so users on browsers without a default mail handler
  // (where mailto silently does nothing) still get something useful.
  const handleEmailClick = () => {
    try {
      navigator.clipboard?.writeText('info@propvetter.com');
    } catch {
      /* clipboard API may be unavailable on insecure origins — ignore */
    }
    setEmailCopied(true);
    window.setTimeout(() => setEmailCopied(false), 2000);
  };

  // Scroll to section function
  const scrollToSection = (id: string) => {
    if (id === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      const headerOffset = window.innerWidth < 768 ? 80 : 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // When arriving via a hash (e.g. /#contact from another page), scroll to it.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Defer to next tick so the section has mounted.
      setTimeout(() => scrollToSection(id), 50);
    }
  }, [location.hash]);

  // Handle contact form
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactFormData({
      ...contactFormData,
      [e.target.id]: e.target.value
    });
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Pack firm + role into the message body so the existing Lambda endpoint
    // (name/email/phone/message) doesn't need a backend change. Same pattern
    // the Consulting form uses.
    const payload = {
      name: contactFormData.name,
      email: contactFormData.email,
      phone: '',
      ...botTrap.signals(),
      message: `[Home inquiry]\nFirm: ${contactFormData.firm || '—'}\nRole: ${contactFormData.role || '—'}\n\n${contactFormData.message}`,
    };

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Message sent!",
          description: "Thanks for reaching out. We'll get back to you soon.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });

        setContactFormData({
          name: '',
          firm: '',
          email: '',
          role: '',
          message: ''
        });
        botTrap.reset();
      } else {
        throw new Error(data.message || "Failed to send message");
      }
    } catch (error) {
      logger.error('Error sending message:', error);
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SiteNav currentPage="home" onScrollTo={scrollToSection} />

      {/* Announcement Banner - Below nav */}
      <div className="bg-gsp-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 text-center">
          <a
            href="#announcement"
            onClick={(e) => { e.preventDefault(); scrollToSection('announcement'); }}
            className="text-white text-sm hover:text-gsp-orange transition-colors"
          >
            <span className="hidden sm:inline"><strong>Announcement:</strong> <span className="underline">AXCS Capital Acquires Propvetter</span></span>
            <span className="sm:hidden"><span className="underline">AXCS Acquires Propvetter</span></span>
          </a>
        </div>
      </div>

      {/* Hero — fills the viewport (minus nav + announcement bar) so the
          visible grid background + symmetric fade has room to breathe.
          Matches the Advisory hero treatment that Eric called out as the
          target look. */}
      <section id="home" className="relative bg-gray-50 overflow-hidden min-h-[calc(100vh-120px)] flex flex-col">
        <GridBackground tone="visible" />

        {/* Single ornament — top-right teal (designer review: ornaments
            were repeating too much; one per hero, none elsewhere). */}
        <div className="absolute -top-10 -right-10 w-72 h-72 text-gsp-teal opacity-10 pointer-events-none">
          <svg viewBox="0 0 200 200" fill="none">
            <path d="M0 200L200 0V60L0 200Z" fill="currentColor" />
            <path d="M60 200L200 60V120L120 200H60Z" fill="currentColor" />
            <path d="M120 200L200 120V200H120Z" fill="currentColor" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 flex flex-col items-center justify-center py-16 w-full">
          <MotionDiv
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
            className="max-w-4xl text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-gsp-text-primary" style={{ lineHeight: 1.15 }}>
              Close deals faster<br />
              with <span className="text-gsp-teal">George Smith Partners.</span>
            </h1>

            <p className="mt-7 text-lg sm:text-xl text-gsp-text-body leading-relaxed max-w-2xl mx-auto">
              AI-powered CRE document analysis. And when you need to go further, our advisory team partners with CRE firms on AI strategy and implementation.
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <button onClick={() => scrollToSection('demo')} className={btnPrimary}>
                Try it now
              </button>
            </div>
          </MotionDiv>

          {/* Module cards — icon + title + one-line subtitle. Subtitles
              kept short on purpose; `truncate` prevents wrap (ellipsis
              would kick in if a card narrows on a smaller viewport). */}
          <div className="mt-16 w-full grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {[
              { icon: TrendingUp, title: 'T12 Financials',  desc: 'Income & expenses, normalized',      delay: 0.10 },
              { icon: BarChart3,  title: 'Rent Rolls',      desc: 'Any rent roll, standardized',        delay: 0.18 },
              { icon: FileText,   title: 'Lease Abstracts', desc: 'Battle-tested, high quality outputs', delay: 0.26 },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <MotionDiv
                  key={card.title}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: card.delay, ease: [0.22, 0.61, 0.36, 1] }}
                >
                  <button
                    type="button"
                    onClick={() => scrollToSection('demo')}
                    className="group w-full h-full text-left bg-white rounded-2xl border border-gray-200 p-6 hover:border-gsp-teal/50 hover:shadow-[0_18px_40px_-18px_rgba(1,36,52,0.22)] hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-4"
                  >
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gsp-teal/10 flex items-center justify-center text-gsp-teal">
                      <Icon className="w-5 h-5" strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-gsp-text-primary truncate">{card.title}</h3>
                      <p className="text-xs text-gsp-text-subtle truncate">{card.desc}</p>
                    </div>
                  </button>
                </MotionDiv>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advisory Callout — navy "stop and read this" moment placed
          directly below the hero so the advisory upsell surfaces early,
          paired with an institutional trust signal. */}
      <section id="advisory-callout" className="relative py-20 sm:py-24 bg-gradient-to-br from-gsp-navy via-gsp-navy to-gsp-navy-light overflow-hidden">
        {/* Light grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.10) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.10) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
        />
        {/* Ornament removed — keeping one ornament per hero, none elsewhere. */}

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Eyebrow badge removed per designer review (read as decorative
              AI-stamped, no information value). */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.9] text-white mb-5">
            Strategy, training, and AI<br />
            <span className="text-gsp-orange">built for your firm.</span>
          </h2>

          <p className="text-lg text-white/80 leading-relaxed mb-10 max-w-2xl mx-auto">
            Built on decades of institutional CRE experience at George Smith Partners and an in-house AI engineering team. We partner with firms on strategy, hands-on training, and custom build. Practitioners, not pitch decks.
          </p>

          {/* Both CTAs now use the site's two button styles (primary
              orange filled + secondary orange outline). Download
              one-pager becomes a CTA button — was floating text. */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <RouterLink to="/consulting" className={btnPrimary}>
              Explore advisory services
            </RouterLink>
            <a
              href="/assets/Propvetter-Advisory-One-Pager.pdf"
              download
              className={btnSecondary}
            >
              Download one-pager
            </a>
          </div>
        </div>
      </section>

      {/* Demo Section — three live modules to try (merged from the old
          Capabilities section, so the "name the modules" and "try them"
          beats happen as one moment, not two). */}
      <DemoSection onContactClick={() => scrollToSection('contact')} />

      {/* Announcement Section */}
      <Section id="announcement" bgColor="bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          {/* Header - Full width, centered */}
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            {/* Eyebrow badge removed (designer review: no info value). */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gsp-text-primary">
              AXCS Capital Acquires Propvetter
            </h2>
          </MotionDiv>

          {/* Two-column layout for body content and card */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column - Body Content */}
            <MotionDiv
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-7"
            >
              <div className="space-y-5 text-gsp-text-body">
                <p className="text-lg leading-relaxed">
                  George Smith Partners, a leading real estate capital markets advisory firm and subsidiary of AXCS Capital, is pleased to announce the acquisition of Propvetter, an AI-powered document analysis platform for commercial real estate.
                </p>

                <p className="text-lg leading-relaxed">
                  By combining GSP's deep expertise in CRE finance and capital markets with Propvetter's document intelligence platform, we are building the next generation of CRE deal analysis and due diligence.
                </p>

                <p className="text-lg leading-relaxed">
                  Together, we're fundamentally reshaping how business is conducted in commercial real estate, enabling faster decisions, deeper insights, and better outcomes across the entire transaction lifecycle.
                </p>
              </div>

              <div className="mt-10">
                <a
                  href="https://www.gspartners.com/about/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={btnSecondary}
                >
                  Learn more about George Smith Partners
                </a>
              </div>
            </MotionDiv>

            {/* Right Column - Visual Element */}
            <MotionDiv
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="relative">
                {/* Brand card */}
                <div className="bg-gradient-to-br from-gsp-navy to-gsp-navy/90 rounded-2xl p-8 shadow-xl">
                  <div className="space-y-6">
                    {/* Logos */}
                    <div className="flex items-center gap-4">
                      <img
                        src="/propvetter_logo_medium.png"
                        alt="Propvetter by GSP"
                        className="h-12 w-auto brightness-0 invert"
                      />
                    </div>

                    {/* Divider */}
                    <div className="border-t border-white/20" />

                    {/* Key stats or highlights */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gsp-teal/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <FileText className="w-4 h-4 text-gsp-teal" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">AI-Powered Analysis</p>
                          <p className="text-white/70 text-sm">Document intelligence for CRE</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gsp-teal/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <BarChart3 className="w-4 h-4 text-gsp-teal" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">Deep CRE Expertise</p>
                          <p className="text-white/70 text-sm">Capital markets leadership</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gsp-teal/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Search className="w-4 h-4 text-gsp-teal" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">Better Outcomes</p>
                          <p className="text-white/70 text-sm">Faster decisions, deeper insights</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative element */}
                <DiagonalStripes className="absolute -bottom-8 -right-8 opacity-20" />
              </div>
            </MotionDiv>
          </div>
        </div>
      </Section>

      {/* Contact Section — teal card with the same white-on-dark grid
          overlay used in the Advisory Callout, so the two "moments" on
          the page share a visual vocabulary. */}
      <section id="contact" className="bg-white py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-gsp-teal rounded-[20px]">
            {/* Light grid overlay (white-on-teal, matches Advisory) */}
            <div
              className="absolute inset-0 pointer-events-none opacity-50"
              style={{
                backgroundImage: `
                  linear-gradient(to right, rgba(255,255,255,0.10) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(255,255,255,0.10) 1px, transparent 1px)
                `,
                backgroundSize: '48px 48px',
              }}
            />

            <div className="relative px-6 sm:px-12 lg:px-16 py-16 sm:py-20">
              {/* Layout mirrors the Advisory page's "Book a consult" form
                  per designer review — contact info column on the left,
                  glassmorphism inquiry form on the right, same field set
                  (Name / Firm / Email / Role / Message). */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-5 text-white">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
                    Get in touch.
                  </h2>
                  <p className="text-white/85 text-lg leading-relaxed mb-8">
                    Have a deal you're looking to finance? Questions about how AI can transform your CRE workflows? We'd love to hear from you.
                  </p>

                  <div className="space-y-4">
                    <a
                      href="mailto:info@propvetter.com"
                      onClick={handleEmailClick}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                        <FaEnvelope className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-white/60">Email</div>
                        <div className="text-white font-medium">
                          {emailCopied ? 'Copied to clipboard!' : 'info@propvetter.com'}
                        </div>
                      </div>
                    </a>
                    <a href="tel:+13105578336" className="flex items-center gap-4 group">
                      <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                        <FaPhone className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-white/60">Phone</div>
                        <div className="text-white font-medium">(310) 557-8336</div>
                      </div>
                    </a>
                    <a
                      href="https://www.linkedin.com/company/propvetter/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                        <FaLinkedin className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-white/60">LinkedIn</div>
                        <div className="text-white font-medium">Propvetter by George Smith Partners</div>
                      </div>
                    </a>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="bg-white/[0.08] backdrop-blur-md border border-white/15 rounded-2xl p-7 sm:p-9">
                    <h3 className="text-white font-bold text-lg mb-5">Tell us about your firm</h3>
                    <form className="space-y-4" onSubmit={handleContactSubmit}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-white text-sm font-medium mb-1.5">
                            Name <span className="text-gsp-orange">*</span>
                          </label>
                          <input
                            id="name"
                            type="text"
                            required
                            value={contactFormData.name}
                            onChange={handleContactChange}
                            placeholder="Your name"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                        <div>
                          <label htmlFor="firm" className="block text-white text-sm font-medium mb-1.5">
                            Firm <span className="text-gsp-orange">*</span>
                          </label>
                          <input
                            id="firm"
                            type="text"
                            required
                            value={contactFormData.firm}
                            onChange={handleContactChange}
                            placeholder="Company name"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="email" className="block text-white text-sm font-medium mb-1.5">
                            Email <span className="text-gsp-orange">*</span>
                          </label>
                          <input
                            id="email"
                            type="email"
                            required
                            value={contactFormData.email}
                            onChange={handleContactChange}
                            placeholder="you@firm.com"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                        <div>
                          <label htmlFor="role" className="block text-white text-sm font-medium mb-1.5">Role</label>
                          <input
                            id="role"
                            type="text"
                            value={contactFormData.role}
                            onChange={handleContactChange}
                            placeholder="Managing Director"
                            className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-white text-sm font-medium mb-1.5">
                          What would you like to discuss?
                        </label>
                        <textarea
                          id="message"
                          rows={4}
                          value={contactFormData.message}
                          onChange={handleContactChange}
                          placeholder="Deal context, AI workflow questions, or anything else…"
                          className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white focus:bg-white/15 transition resize-none"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`${btnPrimary} w-full sm:w-auto disabled:opacity-60`}
                      >
                        {isSubmitting ? 'Sending…' : 'Send inquiry'}
                      </button>
                      {/* Last child: under space-y-4 an earlier position would push the visible fields down. */}
                      <BotTrapField inputRef={botTrap.inputRef} />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default Landing;
